import { Box, Grid, styled } from '@mui/material';
import { FileEarmarkText } from '@styled-icons/bootstrap/FileEarmarkText';
import PartnerAutocompleteWrapper from 'FeatureModules/Activity/Components/Forms/PartnerAutocompleteWrapper';
import SitesAutocompleteWrapper from 'FeatureModules/Activity/Components/Forms/SiteMulitSelectionWrapper';
import documentVisibilityOptions, {
  TVisibilityStatusKeyValue,
} from 'FeatureModules/Document/utils/documentVisibility';
import DatePickerField from 'Components/Forms/DatePickerField';
import DropDown from 'Components/Forms/DropDown';
import TextInputField from 'Components/Forms/TextInputField';
import TextInputFileExtension from 'Components/Forms/TextInputFileExtension';
import {
  documentSchema,
  requestDocumentSchema,
} from 'FeatureModules/Document/schemas/document.schema';
import { FieldWrapper } from 'DesignSystem';
import { FastField as Field, Form, Formik } from 'formik';
import { TCreateDocumentInput } from 'graphql/secure/mutations/types/document-mutation.types';
import isEmpty from 'lodash/isEmpty';
import omitBy from 'lodash/omitBy';
import React, { FC, Fragment, ReactElement, useMemo } from 'react';
import {
  EDocumentCategory,
  EDocumentVisibilityStatus,
  IDocument,
  TDocumentCategory,
} from 'FeatureModules/Document/types/document.types';
import { IBaseSite } from 'FeatureModules/Site/types/site.types';
import CertificationAutoComplete from '../CertificationAutoComplete';
import DocumentCategory from '../../utils/documentCategory.utils';

interface IRenderProps {
  submitForm: () => Promise<void>;
  isSubmitting: boolean;
}

interface IEditDocumentDialogFormProps {
  initialDocument?: Partial<
    Omit<IDocument, 'linkedSites'> & {
      linkedSites?: IBaseSite[];
      certificateCatalogItemId?: string;
    }
  >;
  disableLinkedSites?: boolean;
  disableOwnedBy?: boolean;

  isRequestDocument?: boolean;
  children: (renderProps: IRenderProps) => ReactElement;
  onSubmit: (values: TCreateDocumentInput) => void;
}

const CertificationContainer = styled(Grid)(({ theme }) => ({
  '&.MuiGrid-root': {
    paddingTop: theme.spacing(1.5),
  },
}));

const CertificationBox = styled(Box)(({ theme }) => ({
  background: theme.custom.colors.backgroundLight,
  borderRadius: 4,
  padding: 12,
}));

const EditDocumentDialogForm: FC<IEditDocumentDialogFormProps> = ({
  initialDocument,
  disableLinkedSites,
  disableOwnedBy,
  onSubmit,
  children = () => null,
  isRequestDocument = false,
}) => {
  const documentCategoryOptions = Object.values(EDocumentCategory);

  const visibilityOptions = useMemo(() => {
    if (isRequestDocument)
      return documentVisibilityOptions.filter(
        option => option.key !== EDocumentVisibilityStatus.PRIVATE
      );

    return documentVisibilityOptions;
  }, [isRequestDocument]);

  const sanitizedInitialDocument = initialDocument ? omitBy(initialDocument, isEmpty) : {};

  const initialValues: TCreateDocumentInput = {
    ...(isRequestDocument ? requestDocumentSchema.default() : documentSchema.default()),
    ...sanitizedInitialDocument, // Remove undefined/null values to not override defaults
    // set initial value to visibility public if it is a request document.
    // By default all documents are set to private when uploading. This is not intended if document are getting shared.
    visibility: isRequestDocument ? EDocumentVisibilityStatus.PUBLIC : initialDocument?.visibility,
  } as TCreateDocumentInput;

  return (
    <Formik<TCreateDocumentInput>
      initialValues={initialValues}
      validationSchema={isRequestDocument ? requestDocumentSchema : documentSchema}
      enableReinitialize
      onSubmit={values => onSubmit(values)}
    >
      {({ submitForm, isSubmitting, setFieldValue, values, errors }) => {
        const category = values.category;
        // display the certification field if document category is set to certificate
        const isCertificate = category === EDocumentCategory.CERTIFICATE;
        const disableDateFields = !category;

        // For some documents the expiry date is dependent on the issued date.
        // This is why we are checking how many years the document has been valid for
        const yearsFromIssuedDate = category
          ? DocumentCategory[category]?.yearsFromIssuedDate
          : undefined;

        return (
          <Fragment>
            <Form>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Box display="flex" alignItems="center">
                    <Box mr={1}>
                      <FileEarmarkText size={35} />
                    </Box>
                    <Field
                      component={TextInputFileExtension}
                      name="title"
                      variant="outlined"
                      fullWidth
                      data-testid="document-name-input"
                    />
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <FieldWrapper label="Document type">
                    <Field
                      component={DropDown}
                      errorMsg={errors['category']}
                      name="category"
                      label="Select type"
                      variant="outlined"
                      fullWidth
                      currentValue={values?.category}
                      options={documentCategoryOptions}
                      onChange={(item: TDocumentCategory) => setFieldValue('category', item)}
                      renderItems={(item: TDocumentCategory) => (
                        <div>{DocumentCategory[item].label}</div>
                      )}
                      data-testid="document-category-dropdown"
                    />
                  </FieldWrapper>
                </Grid>
                <Grid item xs={6}>
                  <FieldWrapper
                    label="Confidentiality"
                    tooltip={{
                      helperText: (
                        <div>
                          <p>
                            The confidentiality level decides who can view your document. Only users
                            from your organisation can edit this setting.
                          </p>
                          <p>
                            <b>Public</b>: Visible to everyone, including consumers when document is
                            used as proof for an impact claim.
                          </p>

                          {/* <p>
                              <b>Partners</b>: Allows actors you are connected to in the platform to view the document. The contents of the document is hidden for consumers and any other external users.
                            </p> */}

                          <p>
                            <b>Private</b>: Users from your organisation can view this document. It
                            can also be reviewed by the system when used as proof for impact claims,
                            but is hidden to all others.
                          </p>
                        </div>
                      ),
                      variant: 'INFO',
                    }}
                  >
                    <Field
                      component={DropDown}
                      name="visibility"
                      variant="outlined"
                      fullWidth
                      currentValue={values?.visibility}
                      options={visibilityOptions}
                      keyField="key"
                      onChange={(item: EDocumentVisibilityStatus) =>
                        setFieldValue('visibility', item)
                      }
                      data-testid="document-visibility-dropdown"
                      renderItems={(item: TVisibilityStatusKeyValue) => (
                        <Box display="flex" gap={1.5} justifyContent="center">
                          <span>{item.icon}</span>
                          <span>{item.value}</span>
                        </Box>
                      )}
                    />
                  </FieldWrapper>
                </Grid>

                {isCertificate && (
                  <CertificationContainer item xs={12}>
                    <CertificationBox>
                      <FieldWrapper label="Select certification">
                        <Field
                          name="certificateCatalogItemId"
                          data-testid="document-certificate"
                          component={CertificationAutoComplete}
                        />
                      </FieldWrapper>
                    </CertificationBox>
                  </CertificationContainer>
                )}

                <Grid item xs={6}>
                  <PartnerAutocompleteWrapper
                    name="ownedBy"
                    disabled={disableOwnedBy}
                    data-testid="document-owned-by-selection"
                    size="medium"
                    labelVariant="medium"
                  />
                </Grid>

                <Grid item xs={6}>
                  <SitesAutocompleteWrapper
                    name="linkedSites"
                    disabled={disableLinkedSites}
                    filterByOwnerId={values.ownedBy?.id}
                    label="Link to site(s) or cluster(s)"
                    data-testid="document-linked-sites-selection"
                    helperText="Select a site to link this document to, if the document refers to a specific site (e.g. a risk mitigation proof document for a certain farm)."
                    onChange={linkedSites => {
                      // If the linked sites change, we need update the ownedBy field accordingly with the first sites owner
                      if (linkedSites && linkedSites.length > 0) {
                        const firstSiteOwner = linkedSites[0].ownedBy;
                        setFieldValue('ownedBy', firstSiteOwner);
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={6}>
                  <FieldWrapper label="Issued/capture date">
                    <DatePickerField
                      disabled={disableDateFields}
                      name="issuedDate"
                      placeholder="Select date"
                      data-testid="date-picker-issued-date"
                      placement="TOP"
                    />
                  </FieldWrapper>
                </Grid>
                <Grid item xs={6}>
                  <FieldWrapper
                    label="Expiry Date"
                    info={
                      !!yearsFromIssuedDate
                        ? `${yearsFromIssuedDate} year(s) validity period`
                        : undefined
                    }
                  >
                    <DatePickerField
                      disabled={disableDateFields || !!yearsFromIssuedDate}
                      name="expiryDate"
                      placeholder="Select date"
                      data-testid="date-picker-expiry-date"
                      dependentField="issuedDate"
                      yearsAddedFromDependentField={yearsFromIssuedDate}
                      placement="TOP"
                      align="LEFT"
                    />
                  </FieldWrapper>
                </Grid>

                <Grid item xs={12}>
                  <Field
                    name="comment"
                    component={TextInputField}
                    variant="outlined"
                    fullWidth
                    multiline
                    maxlength={300}
                    maxRows={5}
                    minRows={3}
                    placeholder="Enter note"
                    data-testid="document-comment-input"
                    label="Note"
                    helperText="Add notes to your uploaded document to provide additional context, such as explaining its purpose or relevance (for example, if it is intended for risk mitigation)."
                  />
                </Grid>
              </Grid>
            </Form>
            {children({
              submitForm,
              isSubmitting,
            })}
          </Fragment>
        );
      }}
    </Formik>
  );
};

export default EditDocumentDialogForm;
